var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { CpslModal, defineCustomElements, generateTheme } from '@usecapsule/react-components';
import { ModalContent } from './components/index.js';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { useCapsuleStore, useModalStore, useUserInfoStore, useThemeStore } from './stores/index.js';
import { ModalStep } from './utils/steps.js';
import { DEFAULTS } from './constants/defaults.js';
import { useGoBack } from './hooks/useGoBack.js';
import { Network, getNetwork } from '@usecapsule/web-sdk';
gsap.registerPlugin(useGSAP);
defineCustomElements();
export const CapsuleModal = forwardRef((_a, ref) => {
    var { capsule, isOpen, theme, appName, logo, disableEmailLogin = false, disablePhoneLogin = false, oAuthMethods, bareModal = false, className, onRampConfig, networks = [Network.ETHEREUM], onModalStepChange, onExpandModalChange, onClose } = _a, rest = __rest(_a, ["capsule", "isOpen", "theme", "appName", "logo", "disableEmailLogin", "disablePhoneLogin", "oAuthMethods", "bareModal", "className", "onRampConfig", "networks", "onModalStepChange", "onExpandModalChange", "onClose"]);
    const modalContentRef = useRef(null);
    const updateThemeState = useThemeStore(state => state.updateState);
    const setWebAuthURLForLogin = useModalStore(state => state.setWebAuthURLForLogin);
    const setWebAuthURLForCreate = useModalStore(state => state.setWebAuthURLForCreate);
    const currentStep = useModalStore(state => state.step);
    const setOnModalStepChange = useModalStore(state => state.setOnModalStepChange);
    const setOnRampConfig = useModalStore(state => state.setOnRampConfig);
    const setNetworks = useModalStore(state => state.setNetworks);
    const setStep = useModalStore(state => state.setStep);
    const setCapsule = useCapsuleStore(state => state.setCapsule);
    const setEmail = useUserInfoStore(state => state.setEmail);
    const hasPreviousStep = useModalStore(state => state.hasPreviousStep());
    const goBack = useGoBack();
    const [isModalMounted, setIsModalMounted] = useState(false);
    const [hasFinishedAnimation, setHasFinishedAnimation] = useState(false);
    const [modalExpanded, setModalExpanded] = useState(false);
    useImperativeHandle(ref, () => {
        return {
            goBack() {
                goBack();
            },
            canGoBack() {
                return hasPreviousStep;
            },
            isModalExpanded() {
                return modalExpanded;
            },
            toggleModalExpanded() {
                setModalExpanded(curr => !curr);
            },
            currentStep() {
                return currentStep;
            },
            handleModalClose() {
                var _a;
                (_a = modalContentRef === null || modalContentRef === void 0 ? void 0 : modalContentRef.current) === null || _a === void 0 ? void 0 : _a.handleModalClose();
            },
        };
    }, [hasPreviousStep, modalExpanded, currentStep]);
    // This will run on mount and on isOpen change but won't cause a rerender unless step or email changes
    const initModal = () => __awaiter(void 0, void 0, void 0, function* () {
        if (yield capsule.isFullyLoggedIn()) {
            setStep(ModalStep.LOGIN_DONE);
        }
        else if (currentStep === ModalStep.LOGIN_DONE ||
            currentStep === ModalStep.TWO_FACTOR_DONE ||
            currentStep === ModalStep.SETUP_2FA ||
            currentStep === ModalStep.SECRET ||
            currentStep === ModalStep.BIOMETRIC_LOGIN ||
            currentStep === ModalStep.BIOMETRIC_CREATION ||
            currentStep === ModalStep.WALLET_CREATION_DONE) {
            setStep(ModalStep.SIGN_UP);
            setWebAuthURLForLogin();
            setWebAuthURLForCreate();
        }
        setEmail(capsule.getEmail());
    });
    useEffect(() => {
        onExpandModalChange === null || onExpandModalChange === void 0 ? void 0 : onExpandModalChange(modalExpanded);
    }, [modalExpanded]);
    useEffect(() => {
        setOnModalStepChange(onModalStepChange);
    }, [onModalStepChange]);
    useEffect(() => {
        setOnRampConfig(onRampConfig);
    }, [onRampConfig]);
    useEffect(() => {
        setNetworks(networks.map(getNetwork));
    }, [networks]);
    useEffect(() => {
        var _a;
        updateThemeState({ logo, appName, oAuthLogoVariant: (_a = theme === null || theme === void 0 ? void 0 : theme.oAuthLogoVariant) !== null && _a !== void 0 ? _a : 'default', bareModal });
    }, [logo, appName, theme === null || theme === void 0 ? void 0 : theme.oAuthLogoVariant, bareModal]);
    useEffect(() => {
        if (theme) {
            const isDark = generateTheme(theme);
            updateThemeState({ isDark });
        }
    }, [theme]);
    // Set Capsule instance & init on mount
    useEffect(() => {
        if (capsule) {
            initModal();
            setCapsule(capsule);
            if (bareModal) {
                setIsModalMounted(true);
            }
        }
        else {
            console.error('A Capsule instance must be provided.');
        }
    }, []);
    useEffect(() => {
        setCapsule(capsule);
    }, [capsule]);
    // Init modal with proper steps on isOpen change
    useEffect(() => {
        if (capsule) {
            // If animating out, reset state after animation is done
            setTimeout(() => {
                initModal();
            }, isOpen ? 0 : DEFAULTS.ANIMATION_DURATION * 1000);
        }
    }, [isOpen]);
    const handleModalEntering = () => {
        setIsModalMounted(true);
    };
    const handleModalEntered = () => {
        setHasFinishedAnimation(true);
    };
    const handleModalExited = () => {
        setHasFinishedAnimation(false);
        setIsModalMounted(false);
    };
    if (!capsule) {
        return null;
    }
    if (disableEmailLogin && disablePhoneLogin && !(oAuthMethods === null || oAuthMethods === void 0 ? void 0 : oAuthMethods.length)) {
        console.error('At least one OAuth method must be provided if email and phone login are disabled.');
        return null;
    }
    return (_jsx(CpslModal, { enterTransitionDuration: DEFAULTS.ANIMATION_DURATION, exitTransitionDuration: DEFAULTS.ANIMATION_DURATION, footerExpanded: modalExpanded, open: isOpen, onCpslModalExited: handleModalExited, onCpslModalEntered: handleModalEntered, onCpslModalEntering: handleModalEntering, onCpslModalRequestClose: onClose, noOverlay: bareModal, className: className, children: isModalMounted && (_jsx(ModalContent, Object.assign({ hasFinishedAnimation: hasFinishedAnimation, oAuthMethods: oAuthMethods, disableEmailLogin: disableEmailLogin, disablePhoneLogin: disablePhoneLogin, setModalExpanded: setModalExpanded, onClose: onClose }, rest))) }));
});
